import React, { useRef, useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { isCancel } from 'axios';

import {
  Autocomplete,
  Avatar,
  Box,
  Button,
  IconButton,
  Icons,
  Text,
  useTheme,
  Chip,
  AULA_AVATAR_BG_COLOUR,
} from '@ublend-npm/aulaui-core';

import { usersSearch } from '@core/api/search';
import { SelectedUser } from '@core/conversations/types';
import { currentUserId } from '@core/selectors/user';
import { AulaState } from '@core/types/state';
import { getCurrentSpaceId } from '@core/selectors/space';
import useBreakpoints from '@app/hooks/useBreakpoints';
import pushRoute from '@app/utils/routing';
import ConversationParticipantsListResponsiveContainer from './MessageListAndTools/ConversationModal/modals/ConversationParticipantsListResponsiveContainer';
import { DARK } from '@core/constants/theme';
import { isInstructorInSpace } from '@core/selectors/classroom';

type InviteParticipantsProps = {
  onClose?: () => void;
  conversationId?: string;
};

const TIMEOUT_MS = 500;

const InviteParticipants = ({
  onClose,
  conversationId,
}: InviteParticipantsProps) => {
  const [searchText, setSearchText] = useState('');
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [loadingInvite, setLoadingInvite] = useState(false);
  const [options, setOptions] = useState<SelectedUser[] | null>([]);
  const [selectedParticipants, setSelectedParticipants] = useState<
    SelectedUser[]
  >([]);
  const [showViewParticipants, setShowViewParticipants] = useState(false);
  const [waitingForUpdate, setWaitingForUpdate] = useState(false);
  const [expectedParticipantCount, setExpectedParticipantCount] = useState(0);

  const timeoutId = useRef<NodeJS.Timeout | null>(null);
  const { palette } = useTheme();
  const isDarkMode = palette?.mode === DARK;
  const { lg } = useBreakpoints();
  const spaceId = useSelector(getCurrentSpaceId);
  const dispatch = useDispatch();
  const userId = useSelector(currentUserId);
  const isUserInstructorInSpace = useSelector(isInstructorInSpace);

  // Get existing conversation participants to filter them out
  const conversation = useSelector((state: AulaState) => {
    if (!conversationId) return null;
    return state.data?.inboxv2?.conversations?.[conversationId];
  });

  const existingParticipantIds = conversation?.participants
    ? Object.keys(conversation.participants)
    : [];

  const handleUserSearch = (value: string) => {
    if (timeoutId.current) clearTimeout(timeoutId.current);
    setOptions(null);
    timeoutId.current = setTimeout(async () => {
      setLoadingSearch(true);
      try {
        const results = await usersSearch({
          query: value,
          spaceId: null,
          includeEducatorInSpaces: false,
        });
        setOptions(results);
      } catch (error) {
        if (!isCancel(error)) {
          console.error(error);
        }
      }
      setLoadingSearch(false);
      timeoutId.current = null;
    }, TIMEOUT_MS);
  };

  const handleInviteParticipants = async () => {
    if (!conversationId || selectedParticipants.length === 0) return;

    setLoadingInvite(true);
    try {
      const { postParticipant } = await import(
        '../../../../core/conversations/api/conversations'
      );

      for (const participant of selectedParticipants) {
        await postParticipant(
          conversationId,
          participant.objectId || participant.id
        );
      }

      if (lg) {
        const currentCount = Object.keys(
          conversation?.participants || {}
        ).length;
        setExpectedParticipantCount(currentCount + selectedParticipants.length);
        setWaitingForUpdate(true);
      } else {
        pushRoute(`dashboard/${spaceId}/messages/${conversationId}`);

        setTimeout(async () => {
          const { default: openViewParticipants } = await import(
            '../../../../core/conversations/operationsResponsive/openViewParticipants.action'
          );
          dispatch(openViewParticipants());
        }, 500);
      }

      setSelectedParticipants([]);
    } catch (error) {
      console.error('Error inviting participants:', error);
    }
    setLoadingInvite(false);
  };

  // Effect to watch for conversation updates and show modal when ready (large screens)
  useEffect(() => {
    if (lg && waitingForUpdate && conversation) {
      const currentCount = Object.keys(conversation.participants || {}).length;
      if (currentCount >= expectedParticipantCount) {
        setWaitingForUpdate(false);
        setShowViewParticipants(true);
      }
    }
  }, [lg, conversation, waitingForUpdate, expectedParticipantCount]);

  const handleViewParticipantsClose = () => {
    setShowViewParticipants(false);
    onClose?.();
  };

  return (
    <>
      {lg && showViewParticipants && conversation && (
        <ConversationParticipantsListResponsiveContainer
          handleClose={handleViewParticipantsClose}
          groupId={conversationId!}
          boundToSpaceGroup={!!conversation.boundToSpaceGroup}
          openn={showViewParticipants}
          classRoomId={conversation.space || ''}
          userId={userId || ''}
          educatorOnly={!!conversation.educatorOnly}
          conversationData={conversation}
        />
      )}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Text kind="h4">Invite Participants</Text>
        {onClose && (
          <IconButton aria-label="Close Popup" onClick={onClose}>
            <Icons.Close />
          </IconButton>
        )}
      </Box>
      <Box sx={{ marginTop: 2 }}>
        <Autocomplete
          sx={{ maxWidth: 'calc(100dvw - 32px)' }}
          multiple
          chipSize="small"
          limitRows={3}
          label="Enter name of participants"
          loading={loadingSearch}
          options={options || []}
          renderOption={(props, option) => (
            <li {...(props as any)} key={option.id}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Avatar
                  src={option.avatar}
                  fallbackText={`${
                    option?.firstName
                      ? option.firstName.charAt(0).toUpperCase()
                      : 'U'
                  }`}
                  sx={{
                    width: 24,
                    height: 24,
                    backgroundColor: AULA_AVATAR_BG_COLOUR,
                    fontSize: '12px',
                    color: palette.common.white,
                  }}
                />
                <Text
                  sx={{
                    color: palette.text.primary,
                    fontSize: '16px',
                  }}
                >
                  {option.firstName} {option.lastName}
                </Text>
                {isUserInstructorInSpace(option.id, spaceId) && (
                  <Chip
                    label="Educator"
                    size="small"
                    sx={{
                      backgroundColor: isDarkMode
                        ? palette.secondary.main
                        : palette.info.main,
                      borderColor: isDarkMode
                        ? palette.secondary.main
                        : palette.info.main,
                      color: isDarkMode
                        ? palette.common.black
                        : palette.common.white,
                      fontSize: '12px',
                    }}
                  />
                )}
              </Box>
            </li>
          )}
          renderSelectedOption={(option: SelectedUser) => (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                px: 0,
                ml: '-5px',
              }}
            >
              <Avatar
                src={option.avatar}
                fallbackText={`${
                  option?.firstName
                    ? option.firstName.charAt(0).toUpperCase()
                    : 'U'
                }`}
                sx={{
                  width: 18,
                  height: 18,
                  backgroundColor: AULA_AVATAR_BG_COLOUR,
                  fontSize: '10px',
                  color: palette.common.white,
                  paddingLeft: '0px',
                  paddingTop: '1px',
                  position: 'absolute',
                }}
              />
              <Text
                sx={{
                  fontSize: '13px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  display: 'block',
                  ml: '22px',
                }}
              >
                {option.firstName} {option.lastName}
              </Text>
              {isUserInstructorInSpace(option.id, spaceId) && (
                <Chip
                  label="Educator"
                  size="small"
                  sx={{
                    backgroundColor: isDarkMode
                      ? palette.secondary.main
                      : palette.info.main,
                    borderColor: isDarkMode
                      ? palette.secondary.main
                      : palette.info.main,
                    color: isDarkMode
                      ? palette.common.black
                      : palette.common.white,
                    fontSize: '8.9px',
                    maxHeight: '16.44px',
                    marginBottom: '2px',
                  }}
                />
              )}
            </Box>
          )}
          filterOptions={(options) =>
            options.filter(
              (o) =>
                o.id !== userId &&
                !existingParticipantIds.includes(o.id) &&
                !selectedParticipants.map((c) => c.id).includes(o.id)
            )
          }
          noOptionsText={
            searchText === '' || options === null
              ? 'Type to search'
              : 'No matches found'
          }
          inputValue={searchText}
          onInputChange={(_, value) => {
            setSearchText(value);
            handleUserSearch(value);
          }}
          value={selectedParticipants}
          onChange={(_, newVals) => setSelectedParticipants(newVals || [])}
        />
      </Box>
      <Box
        sx={{
          marginTop: 2,
          display: 'flex',
          gap: 2,
          justifyContent: 'end',
        }}
      >
        <Button
          sx={{
            textTransform: 'none',
            '&:hover': {
              borderColor: palette.primary.dark,
            },
          }}
          kind="secondary"
          onClick={onClose}
        >
          Cancel
        </Button>
        <Button
          sx={{
            textTransform: 'none',
            '&:hover': {
              backgroundColor: palette.primary.dark,
            },
          }}
          onClick={handleInviteParticipants}
          loading={loadingInvite}
          disabled={selectedParticipants.length < 1}
        >
          Invite
        </Button>
      </Box>
    </>
  );
};

export default InviteParticipants;
